import proBadge from "images/plan-badges/pro.svg?raw"
import proBadgeWhite from "images/plan-badges/pro-badge-white.svg?raw"
import proPlusBadge from "images/plan-badges/pro_plus.svg?raw"
import proPlusBadgeWhite from "images/plan-badges/pro_plus-badge-white.svg?raw"
import freeBadge from "images/plan-badges/free.svg?raw"
import freeBadgeWhite from "images/plan-badges/free-badge-white.svg?raw"
import starterBadge from "images/plan-badges/starter.svg?raw"
import starterBadgeWhite from "images/plan-badges/starter-badge-white.svg?raw"

export type Tier =
  "free" | "intermediate_pro" | "pro" | "legacy_pro" | "credits_starter" | "credits_pro" | "credits_pro_plus"

// The order here is a little weird. `legacy_pro` is after `pro` (and therefore
// a "greater" tier) so that legacy users have effectively pro permissions.
const TIERS: Array<Tier> = [
  "free",
  "intermediate_pro",
  "pro",
  "legacy_pro",
  "credits_starter",
  "credits_pro",
  "credits_pro_plus",
]

// User-facing plan names for tier slugs. Note the legacy naming inversion:
// the `pro` tier is the Pro+ plan and `intermediate_pro` is the Pro plan.
export const TIER_DISPLAY_NAMES: Record<Tier, string> = {
  free: "Free",
  intermediate_pro: "Pro",
  pro: "Pro+",
  legacy_pro: "Pro",
  credits_starter: "Starter",
  credits_pro: "Pro",
  credits_pro_plus: "Pro+",
}

export const tierDisplayName = (tier: string): string => TIER_DISPLAY_NAMES[tier as Tier] || "Pro"

// Display names for subscription/plan names (e.g. Stripe plan ids), which use
// a different vocabulary than tier slugs: here `pro` means the Pro plan.
export const subscriptionPlanDisplayName = (plan: string): string => {
  if (plan === "pro_plus") return "Pro+"
  if (plan === "pro") return "Pro"
  if (plan === "credits_starter") return "Starter"
  if (plan === "credits_pro") return "Pro"
  if (plan === "credits_pro_plus") return "Pro+"
  return plan
}

// Inline SVG badges keyed by user-facing plan name.
const PLAN_BADGES: Record<string, string> = {
  Free: freeBadge,
  Starter: starterBadge,
  Pro: proBadge,
  "Pro+": proPlusBadge,
}

const PLAN_BADGES_WHITE: Record<string, string> = {
  Free: freeBadgeWhite,
  Starter: starterBadgeWhite,
  Pro: proBadgeWhite,
  "Pro+": proPlusBadgeWhite,
}

export type BadgeVariant = "default" | "white"

export const planNameBadge = (planName: string, variant: BadgeVariant = "default"): string =>
  (variant === "white" ? PLAN_BADGES_WHITE : PLAN_BADGES)[planName] || ""

export const tierBadge = (tier: string, variant: BadgeVariant = "default"): string =>
  planNameBadge(tierDisplayName(tier), variant)

export const maxTier = (tier1: Tier, tier2: Tier): Tier => {
  if (TIERS.indexOf(tier1) < TIERS.indexOf(tier2)) {
    return tier2
  } else {
    return tier1
  }
}

export const compareTier = (tier1: Tier, tier2: Tier): number => {
  return Math.sign(TIERS.indexOf(tier1) - TIERS.indexOf(tier2))
}

// Whether a tier has Pro+-level feature permissions (queries, filters,
// multiple accounts, no applet quota), i.e. the `pro` and `legacy_pro` tiers.
// All credits tiers also qualify: credits users pay per run instead of being
// gated on tier features.
export const hasProPlusFeatures = (tier: string): boolean => compareTier(tier as Tier, "pro") >= 0

// Whether a tier has at least Pro-level feature permissions (multiple
// actions, larger applet quota), i.e. the `intermediate_pro` tier and up.
export const hasProFeatures = (tier: string): boolean => compareTier(tier as Tier, "intermediate_pro") >= 0

// Whether the user already has a paid plan (classic, credits, or legacy).
export const isPaidTier = (tier: string): boolean => tier !== "free"

// Only meaningful for credits tiers. `hasProPlusFeatures` can't answer this: it is true for every
// credits tier, starter included.
export const canUpgradeCredits = (tier: string): boolean => tier !== "credits_pro_plus"
